// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resendOtp, verifyPhone } from '../../services/registration';
import OtpCodeInput from '../../components/codeField/OtpCodeInput';
import { getAccountId, setError } from '../../utils/storage';
import { getApiErrorMessage, handleApiError } from '../../utils/apiError';
import { useResendCountdown } from '../../hooks/useResendCountdown';
import {
    AnalyticsEvent,
    durationSecSince,
    getOtpStartedAt,
    markOtpStarted,
    resolveAnalyticsError,
    setAnalyticsVerificationMethod,
    trackRegistrationFailed,
    useAnalytics,
} from '../../analytics';
import { isTerminalOtpError, useOtpAnalyticsTracking } from '../../analytics/hooks/useOtpAnalyticsTracking';
import useLogger from '../../hooks/useLogger';
import { PHONE_VERIFICATION_ACTIONS, LOG_STATUSES } from '../../logging/LoggingConstants';
import { checkImIdEnabled } from '../../utils/featureFlags';
import RegistrationPage, { PAGE_BODY_TOP_CLASS } from '../../components/layouts/RegistrationPage';
import { cn } from '../../utils/cn';
import phoneIcon from '../../assets/svgs/phone-icon.svg';
import PageHeader from '../../components/PageHeader';
import CustomButton from '../../components/customButton/CustomButton';
import { showOtpErrorToast } from '../../components/toast/showOtpErrorToast';
import PageLoaderOverlay from '../../components/loading/PageLoaderOverlay';
import Message from '../../components/Message';
import ActionLink from '../../components/actionLink/ActionLink';

export default function PhoneVerification() {
    const accountId = getAccountId();
    const navigate = useNavigate();
    const { log } = useLogger();
    const { track } = useAnalytics();
    const [pinCode, setPinCode] = useState('');
    const { counter, reset: resetCountdown, stop: stopCountdown } = useResendCountdown(120);
    const [isLoading, setIsLoading] = useState(false);
    const [otpError, setOtpError] = useState(false);
    const { t } = useTranslation();
    const otpWrapperRef = useRef(null);
    const {
        verifyCount,
        resendCount,
        maxVerifyAttempts,
        maxResends,
        beginVerifyAttempt,
        recordVerifyFailure,
        beginResend,
        recordResendSuccess,
        shouldTrackExpired,
        markExpiredTracked,
        expiredAttempt,
    } = useOtpAnalyticsTracking();

    const otpDisabled = counter === 0 || verifyCount >= maxVerifyAttempts;
    const runOutAttempts = !isLoading && counter === 0 && resendCount >= maxResends;

    useEffect(() => {
        const input = otpWrapperRef.current?.querySelector('input');
        input?.focus();
    }, []);

    useEffect(() => {
        if (counter === 0) {
            setPinCode('');
            setOtpError(false);
            if (shouldTrackExpired()) {
                markExpiredTracked();
                track(AnalyticsEvent.OTP_EXPIRED, {
                    otp_type: 'phone',
                    attempt: expiredAttempt(),
                });
            }
        }
    }, [counter, track, shouldTrackExpired, markExpiredTracked, expiredAttempt]);

    const handleVerify = (value = pinCode) => {
        const attempt = beginVerifyAttempt();
        if (attempt == null) {
            return;
        }

        setIsLoading(true);
        const code = parseInt(value, 10);

        verifyPhone(accountId, code)
            .then(() => {
                log(PHONE_VERIFICATION_ACTIONS.PHONE_VERIFICATION, LOG_STATUSES.SUCCESS);
                track(AnalyticsEvent.OTP_VERIFIED, {
                    otp_type: 'phone',
                    attempt,
                    duration_sec: durationSecSince(getOtpStartedAt('phone')) ?? 0,
                });
                const imIdEnabled = checkImIdEnabled();
                if (!imIdEnabled) {
                    setAnalyticsVerificationMethod('sumsub');
                    track(AnalyticsEvent.VERIFICATION_METHOD_SELECTED, {
                        verification_method: 'sumsub',
                    });
                }
                navigate(imIdEnabled ? '/verification-options' : '/sumsub');
            })
            .catch((e) => {
                const message = getApiErrorMessage(e, 'OtpFail');
                const { error_code, error_message } = resolveAnalyticsError({
                    error: e,
                    fallback: 'OtpFail',
                });

                if (isTerminalOtpError(error_code)) {
                    trackRegistrationFailed({
                        step: 'phone_otp',
                        error_code,
                        error_message,
                    });
                    navigate(`/error?errorCode=error${error_code}`);
                    return;
                }

                track(AnalyticsEvent.OTP_FAILED, {
                    otp_type: 'phone',
                    attempt,
                    duration_sec: durationSecSince(getOtpStartedAt('phone')) ?? 0,
                    error_code,
                    error_message,
                });
                // Restart timer so the next verify attempt is timed independently.
                markOtpStarted('phone');
                setOtpError(true);
                showOtpErrorToast(t('errorOtp'));
                setError(message);

                const { attempt: failedAttempt, attemptsExceeded } =
                    recordVerifyFailure();
                if (failedAttempt >= maxVerifyAttempts) {
                    stopCountdown();
                }
                if (attemptsExceeded) {
                    track(AnalyticsEvent.OTP_ATTEMPTS_EXCEEDED, {
                        otp_type: 'phone',
                        attempt: failedAttempt,
                    });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const handleResend = () => {
        setIsLoading(true);
        const nextResendCount = beginResend();
        setPinCode('');

        resendOtp(accountId, { type: 2 })
            .then(() => {
                log(PHONE_VERIFICATION_ACTIONS.RESEND_PHONE_OTP, LOG_STATUSES.SUCCESS);
                markOtpStarted('phone');
                recordResendSuccess(nextResendCount);
                track(AnalyticsEvent.OTP_RESENT, {
                    otp_type: 'phone',
                    resend_count: nextResendCount,
                });
                resetCountdown();
            })
            .catch((e) => {
                trackRegistrationFailed({
                    step: 'phone_otp',
                    error: e,
                });
                handleApiError(e, navigate);
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            <PageLoaderOverlay active={isLoading} />
            <RegistrationPage
                contentClassName="xl:max-w-[30rem] mx-auto w-full"
                stickyFooter
                footer={
                    runOutAttempts ? (
                        <ActionLink to="/" className="md:w-fit mx-auto mt-4">
                            {t('errorButton')}
                        </ActionLink>
                    ) : null
                }
            >
                <PageHeader title={t('phoneTitle')} lead={t('phoneVerify')} imgSrc={phoneIcon} />
                <div
                    className={cn(
                        'flex w-full flex-col items-center text-center',
                        PAGE_BODY_TOP_CLASS
                    )}
                >
                    <div className="flex justify-center" ref={otpWrapperRef}>
                        <OtpCodeInput
                            id="pinCode"
                            value={pinCode}
                            disabled={otpDisabled}
                            hasError={otpError && !otpDisabled}
                            className="my-3"
                            autoFocus
                            onChange={(value) => {
                                setOtpError(false);
                                setPinCode(value);

                                if (value.length === 5 && !isLoading) {
                                    handleVerify(value);
                                }
                            }}
                        />
                    </div>
                    <div className="mt-4 flex flex-col items-center gap-2 text-center">
                        {counter === 0 && resendCount < maxResends && (
                            <CustomButton variant="text" onClick={handleResend}>
                                {t('codeResend')}
                            </CustomButton>
                        )}

                        <span
                            aria-live="polite"
                            className={cn(
                                'text-base font-medium',
                                counter ? 'text-primary' : 'text-custom-bg-disabled'
                            )}
                        >
                            {counter} {t('seconds')}
                        </span>
                    </div>
                    {runOutAttempts && <Message message={t('runOutAttempts')} />}
                </div>
            </RegistrationPage>
        </>
    );
}
